import { useCallback } from 'react';
import { useLocalStorage } from 'react-use';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { isLocalStorageAvailable } from '~/utils/feature-check';

export const FOLLOWING_COUNT_KEY = 'followingCount';

export interface StoredFollowingCount {
  userId: string;
  count: number;
  timestamp: number;
}

export const useStoredFollowingCount = () => {
  const { session } = useTellerSession();

  const [, storeFollowingCount, removeFollowingCount] =
    useLocalStorage<StoredFollowingCount>(FOLLOWING_COUNT_KEY);

  // If fetching from local storage using hook, data won't be refreshed if we update it
  // and can't also be used as hook dependency, so get the value in a variable here directly
  const storedItem = isLocalStorageAvailable()
    ? window.localStorage.getItem(FOLLOWING_COUNT_KEY)
    : null;

  const followingCount = useCallback(() => {
    try {
      if (!storedItem) {
        return session?.tellerUser?.followeesCount;
      }
      const storedFollowingCount = JSON.parse(storedItem);
      if (
        storedFollowingCount?.userId &&
        storedFollowingCount.userId === session?.tellerUser?.id
      ) {
        const storedDate = storedFollowingCount?.timestamp
          ? new Date(storedFollowingCount.timestamp)
          : null;

        if (storedDate) {
          const now = new Date();
          const diff = Math.abs(now.getTime() - storedDate.getTime());
          const minutes = Math.floor(diff / 1000 / 60);
          if (minutes < 10) {
            const followingCount = storedFollowingCount?.count;

            if (followingCount) {
              return followingCount;
            }
          }
          removeFollowingCount();
        }
      }
      return session?.tellerUser?.followeesCount;
    } catch {
      return session?.tellerUser?.followeesCount;
    }
  }, [
    removeFollowingCount,
    session?.tellerUser?.followeesCount,
    session?.tellerUser?.id,
    storedItem,
  ]);

  const updateStoredFollowingCount = useCallback(
    (count: number) => {
      if (session?.tellerUser) {
        const countObject: StoredFollowingCount = {
          userId: session?.tellerUser.id,
          count,
          timestamp: Date.now(),
        };
        storeFollowingCount(countObject);
      }
    },
    [session?.tellerUser, storeFollowingCount]
  );

  const addFollowingCount = useCallback(() => {
    const count = followingCount();
    if (count) {
      updateStoredFollowingCount(count + 1);
    }
  }, [followingCount, updateStoredFollowingCount]);

  const substractFollowingCount = useCallback(() => {
    const count = followingCount();
    if (count) {
      updateStoredFollowingCount(count - 1);
    }
  }, [followingCount, updateStoredFollowingCount]);

  return {
    followingCount,
    updateStoredFollowingCount,
    addFollowingCount,
    substractFollowingCount,
  };
};
